/**
 * 课程 SKU 只读详情抽屉（subject-course-sku:SC-17 前端）。
 *
 * 列表点击 SKU 编码打开，以 Descriptions 只读展示全部字段；
 * 引用商品经 listProducts({ skuId }) 回填，无引用时空态「暂无在售商品引用」。
 */
import { useEffect, useState } from 'react';
import { Descriptions, Drawer, Empty, List, Typography } from 'antd';
import type { CourseSkuItem } from '../../api/course-skus.api';
import { productApi, type ProductItem } from '../../api/products.api';
import {
  deductionUnitLabel,
  gradeStageLabel,
  shelfStatusLabel,
} from '../../api/labels';
import { MoneyDisplay } from '../../components/MoneyDisplay';

const { Text } = Typography;

export interface SkuDetailDrawerProps {
  /** 当前查看的 SKU */
  sku: CourseSkuItem | null;
  /** 关联科目名称（由列表页派生后传入，避免抽屉内部再查） */
  subjectName?: string;
  /** 关闭抽屉 */
  onClose: () => void;
}

/** 状态标签文案颜色映射。 */
const statusColor: Record<string, string> = {
  DRAFT: 'default',
  ON_SHELF: 'green',
  OFF_SHELF: 'orange',
  DISABLED: 'red',
};

export function SkuDetailDrawer({ sku, subjectName, onClose }: SkuDetailDrawerProps) {
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const open = sku !== null;

  useEffect(() => {
    if (!sku) {
      setProducts([]);
      return;
    }
    setLoadingRefs(true);
    productApi
      .listProducts({ skuId: sku.id, page: 1, pageSize: 100 })
      .then((r) => setProducts(r.items))
      .catch(() => setProducts([]))
      .finally(() => setLoadingRefs(false));
  }, [sku]);

  if (!sku) {
    return <Drawer open={false} onClose={onClose} />;
  }
  return (
    <Drawer
      open={open}
      width={560}
      title={`SKU详情 · ${sku.code}`}
      onClose={onClose}
      destroyOnClose
    >
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="SKU编码">{sku.code}</Descriptions.Item>
        <Descriptions.Item label="名称">{sku.name}</Descriptions.Item>
        <Descriptions.Item label="科目">{subjectName ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="年级/学段">{gradeStageLabel[sku.gradeStage]}</Descriptions.Item>
        <Descriptions.Item label="教材版本">{sku.textbook}</Descriptions.Item>
        <Descriptions.Item label="标价">
          <MoneyDisplay amountInCents={sku.listPrice} />
        </Descriptions.Item>
        <Descriptions.Item label="课时数">{sku.entitlementQuantity}</Descriptions.Item>
        <Descriptions.Item label="试课赠送次数">{sku.trialSessionCount}</Descriptions.Item>
        <Descriptions.Item label="扣减单位">{deductionUnitLabel[sku.deductionUnit]}</Descriptions.Item>
        <Descriptions.Item label="有效期（天）">{sku.validityDays}</Descriptions.Item>
        <Descriptions.Item label="每班最多人数">{sku.maxClassCapacity}</Descriptions.Item>
        <Descriptions.Item label="最多班级数">{sku.maxClassCount}</Descriptions.Item>
        <Descriptions.Item label="有效班级数">{sku.activeClassRefCount}</Descriptions.Item>
        <Descriptions.Item label="状态">
          <Text style={{ color: statusColor[sku.status] ?? undefined }}>
            {shelfStatusLabel[sku.status]}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="更新时间">
          {sku.updatedAt ? new Date(sku.updatedAt).toLocaleString('zh-CN') : '-'}
        </Descriptions.Item>
      </Descriptions>

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        引用商品
      </Text>
      {products.length === 0 && !loadingRefs ? (
        <Empty description="暂无在售商品引用" imageStyle={{ height: 40 }} />
      ) : (
        <List
          size="small"
          loading={loadingRefs}
          dataSource={products}
          renderItem={(p) => (
            <List.Item>
              {p.name}
              {p.status !== 'ON_SHELF' ? `（${shelfStatusLabel[p.status] ?? p.status}）` : ''}
            </List.Item>
          )}
        />
      )}
    </Drawer>
  );
}
